import { FC, useState, ChangeEvent } from 'react';
import { Drawer, Grid, LinearProgress, TextField, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import styled from '@emotion/styled';
import { useMutation } from '@apollo/client';

import {
  UpdateCommentsOfSubmitDocument,
  UpdateCommentsOfSubmitMutation,
  UpdateCommentsOfSubmitMutationVariables,
  PossibleVariation,
} from '../../../../generated/graphql';
import globalState from '../../../../operations/commands';
import { Container as DrawerContainer, Footer as DrawerFooter } from '../../compound/Drawer';
import { DrawerHeader, DrawerTitle, DrawerContent, PrimaryButton, SecondaryButton } from './common'; // TODO: move DrawerXXX in the compound if it's generically useful
import { BodySmall as _BodySmall, BodyBoldStyle, LabelExtraSmall } from '../../primitive/TypographyUI';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';
import { SPACING_REGULAR } from '../../../constant/spacing';
import mutationVariables from '../../../../utility/mutationVariables';

const SubmitCommentDisplay: FC<{ variation: PossibleVariation; refetch: () => void }> = ({ variation, refetch }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [comments, setComments] = useState({
    submit_comment: variation.submit_comment,
    submit_comment_changes: variation.submit_comment_changes,
  });

  const [updateSubmitComments] = useMutation<UpdateCommentsOfSubmitMutation, UpdateCommentsOfSubmitMutationVariables>(
    UpdateCommentsOfSubmitDocument,
    {
      context: { clientName: 'planbcd' },
    },
  );

  const toggleDrawer = () => setIsOpen((prev) => !prev);
  const onChangeSubmitComment = (e: ChangeEvent<HTMLInputElement>) => {
    setComments((prev) => {
      return {
        ...prev,
        submit_comment: e.target.value,
      };
    });
  };
  const onChangeSubmitCommentChanges = (e: ChangeEvent<HTMLInputElement>) => {
    setComments((prev) => {
      return {
        ...prev,
        submit_comment_changes: e.target.value,
      };
    });
  };

  const onClickSubmit = () => {
    setIsProcessing(true);
    updateSubmitComments(
      mutationVariables({
        id: variation.id,
        variation: {
          submit_comment: comments.submit_comment,
          submit_comment_changes: comments.submit_comment_changes,
        },
      }),
    )
      .then(() => {
        refetch();
        setIsProcessing(false);
        toggleDrawer();
      })
      .catch(() => {
        globalState.setErrors({
          message: '変更した内容の更新ができませんでした',
          name: 'NonFatalError',
          context: 'planbcd',
        });
        setIsProcessing(false);
        toggleDrawer();
      });
  };

  return (
    <SubmitCommentFlexWrapper>
      <BodyBold>変更した内容</BodyBold>
      <div>
        <LabelExtraSmall>デザイン案の変更意図</LabelExtraSmall>
        <BodySmall>{variation?.submit_comment ? variation.submit_comment : '記入なし'}</BodySmall>
      </div>
      {variation?.banner_image_url || variation?.display_popup?.id ? null : (
        <div>
          <LabelExtraSmall>デザイン案の変更箇所</LabelExtraSmall>
          <BodySmall>{variation?.submit_comment_changes ? variation.submit_comment_changes : '記入なし'}</BodySmall>
        </div>
      )}
      {variation?.permissions?.update_submit_comment?.permit && (
        <SecondaryButton onClick={toggleDrawer}>
          <EditIcon />
          <span style={{ paddingLeft: '5px' }}>編集する</span>
        </SecondaryButton>
      )}
      <Drawer anchor="right" open={isOpen}>
        <DrawerContainer>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>変更した内容の変更</DrawerTitle>
            </DrawerHeader>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CommentLabel>デザイン案の変更意図</CommentLabel>
                <CommentField
                  variant={`outlined`}
                  multiline
                  minRows={5}
                  value={comments.submit_comment}
                  onChange={onChangeSubmitComment}
                />
              </Grid>
              <Grid item xs={12}>
                <CommentLabel>デザイン案の変更箇所</CommentLabel>
                <CommentField
                  variant={`outlined`}
                  multiline
                  minRows={5}
                  value={comments.submit_comment_changes}
                  onChange={onChangeSubmitCommentChanges}
                />
              </Grid>
            </Grid>
          </DrawerContent>
          <DrawerFooter>
            <SecondaryButton onClick={toggleDrawer}>キャンセル</SecondaryButton>
            <PrimaryButton
              disabled={
                variation.submit_comment === comments.submit_comment &&
                variation.submit_comment_changes === comments.submit_comment_changes
              }
              onClick={onClickSubmit}
            >
              保存する
            </PrimaryButton>
          </DrawerFooter>
          {isProcessing && <LinearProgress />}
        </DrawerContainer>
      </Drawer>
    </SubmitCommentFlexWrapper>
  );
};

const SubmitCommentFlexWrapper = styled('div')(`
  flex: 1 0 40%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 2rem;
  padding-bottom: 4rem;
`);

const CommentField = styled(TextField)(`
  width: 100%;
  border-radius: 8px;
  '& .MuiOutlinedInput-root': {
    color: ${COLOR_TEXT_MAIN};
    border-radius: 8px;
    font-size: 14px;
    height: 118px;
    '& fieldset': {
      outline: none;
      border: 1px solid #E6E7E8;
    };
    '&:hover fieldset': {
      outline: none;
      border: 1.5px solid #E6E7E8;
    };
    '&.Mui-focused fieldset': {
      outline: none;
      border: 1.5px solid #707889;
    };
    '&.Mui-error fieldset': {
      border: 1.5px solid #FE7877;
    };
    '&.Mui-error input': {
      color: #FE7877;
    };
  };
  '&.MuiOutlinedInput-notchedOutline': {
    fieldset: {
      outline: none;
      border: 1.5px solid #707889;
    };
  };
`);

const BodyBold = styled('div')({
  ...BodyBoldStyle,
});

const BodySmall = styled(_BodySmall)({
  margin: `${SPACING_REGULAR}px 0`,
});

const CommentLabel = styled(Typography)(`
  font-size: 12px;
  color: ${COLOR_TEXT_MAIN};
  margin-bottom: 9px;
`);

export default SubmitCommentDisplay;
